<template>
  <v-card
    class="mb-4 px-2 pb-2 multi-specials-card"
    :style="{ 'z-index': specialsQty - specialPosition }"
  >
    <table id="multi-specials-table">
      <thead class="multi-specials-table-header">
        <tr class="multi-specials-title-row">
          <th class="header-cell actions-column">
            Actions {{ specialsQty }} / {{ specialPosition }}
          </th>
          <th class="header-cell special-type-column">Special Type</th>
          <th class="header-cell special-item-column">
            <template v-if="hasSelectedSpecialType">
              <template v-if="special.selectedSpecialType === BY_FAMILY_GROUP"
                >Family Group</template
              >
              <template v-if="special.selectedSpecialType === BY_PRODUCT"
                >Product</template
              >
            </template>
            <template v-else>Special Selection(s)</template>
          </th>
          <th class="header-cell start-date-column">Start Date</th>
          <th class="header-cell end-date-column">End Date</th>
          <th class="header-cell target-type-column">Target Type</th>
          <th class="header-cell target-item-column">
            <template v-if="hasSelectedTargetType">
              <template v-if="special.targetType === TARGET_TYPE.BANNER_GROUP"
                >Banner Groups(s)</template
              >
              <template
                v-if="special.targetType === TARGET_TYPE.MULTI_STORE_OPERATOR"
                >Multi-Store Operator(s)</template
              >
              <template
                v-if="special.targetType === TARGET_TYPE.RETAILER_STORE"
                >{{ isILR ? 'Customer(s)' : 'Retailer(s)' }}</template
              >
            </template>
            <template v-else>Target Selection(s)</template>
          </th>
          <th class="header-cell pricing-column">Pricing</th>
          <th class="header-cell validation-column">Ready</th>
        </tr>

        <tr class="multi-specials-data-row">
          <td class="actions-column">
            <v-btn
              :disabled="!isRowValid"
              icon
              v-tooltip="{
                content: 'Add Special',
                visible: $vuetifyDisplay.mdAndDown ? false : undefined
              }"
              @click="$emit('add')"
            >
              <v-icon class="action-icon">mdi-plus-circle-outline</v-icon>
            </v-btn>

            <v-btn
              :disabled="isOnlyRow"
              icon
              v-tooltip="{
                content: 'Remove Special',
                visible: $vuetifyDisplay.mdAndDown ? false : undefined
              }"
              @click="$emit('remove')"
            >
              <v-icon class="action-icon">mdi-minus-circle-outline</v-icon>
            </v-btn>

            <v-btn
              :disabled="!isRowValid && !isDuplicate"
              icon
              v-tooltip="{
                content: isDuplicate
                  ? 'Duplicated Special'
                  : 'Duplicate Special',
                visible: $vuetifyDisplay.mdAndDown ? false : undefined
              }"
              @click="onDuplicateClick"
            >
              <v-icon class="copy-icon" :class="{ duplicate: isDuplicate }"
                >mdi-content-duplicate</v-icon
              >
            </v-btn>
            <v-btn
              icon
              v-tooltip="{
                content: 'Reset Special to initial values',
                visible: $vuetifyDisplay.mdAndDown ? false : undefined
              }"
              @click="resetSpecial"
            >
              <v-icon class="action-icon">mdi-backup-restore</v-icon>
            </v-btn>
          </td>

          <td class="special-type-column">
            <v-select
              label="Select Type *"
              attach
              dense
              required
              outlined
              v-model="special.selectedSpecialType"
              :background-color="backgroundColor"
              :error="!special.selectedSpecialType || isDuplicate"
              :items="specialTypes"
              :rules="[(v) => !!v || 'Special Type required']"
              @change="onFieldChange('specialTypes', $event)"
            />
          </td>

          <td class="special-item-column">
            <template v-if="hasSelectedSpecialType">
              <template v-if="special.selectedSpecialType === BY_FAMILY_GROUP">
                <v-autocomplete
                  label="Select Family Group *"
                  attach
                  outlined
                  dense
                  v-model="special.selectedFamilyGroup"
                  :background-color="backgroundColor"
                  :error="!special.selectedFamilyGroup || isDuplicate"
                  :disabled="!special.selectedSpecialType"
                  :items="familyGroups"
                  item-disabled="disabled"
                  :rules="[(v) => !!v || 'Family Group required']"
                  @change="onFieldChange('selectedFamilyGroup', $event)"
                >
                  <template v-slot:selection>
                    {{ special.selectedFamilyGroup.name }}
                  </template>
                </v-autocomplete>
                <div
                  v-if="showUnavailableProductAlert"
                  class="inline-price-warning"
                  v-tooltip="upcomingPriceChangeWarningText"
                >
                  <v-icon class="inline-price-warning__icon" small>
                    mdi-alert
                  </v-icon>
                  <span class="inline-price-warning__text">
                    {{ upcomingPriceChangeWarningText }}
                  </span>
                </div>
              </template>
              <template v-if="special.selectedSpecialType === BY_PRODUCT">
                <v-autocomplete
                  label="Select Product *"
                  attach
                  outlined
                  dense
                  clearable
                  v-model="special.selectedProduct"
                  :background-color="backgroundColor"
                  :error="!special.selectedProduct || isDuplicate"
                  :disabled="isProductSelectionDisabled"
                  :loading="isProductSelectionLoading"
                  :no-data-text="
                    isProductSelectionLoading
                      ? 'Loading products...'
                      : 'No data available'
                  "
                  :items="visibleProducts"
                  item-title="text"
                  item-text="text"
                  item-value="value"
                  item-disabled="disabled"
                  :rules="[
                    (v) => !!v || 'Product required (Live products only)'
                  ]"
                  v-model:search-input="productSearchInput"
                  @focus="emitEnsureProducts"
                  @click:clear="clearSelectedProduct"
                  @change="handleProductChange"
                >
                  <template v-slot:selection>
                    {{ special.selectedProduct.supplierItemId }} /
                    {{ special.selectedProduct.tradeItemDescription }}
                  </template>
                </v-autocomplete>
                <div
                  v-if="showUnavailableProductAlert"
                  class="inline-price-warning"
                  v-tooltip="upcomingPriceChangeWarningText"
                >
                  <v-icon class="inline-price-warning__icon" small>
                    mdi-alert
                  </v-icon>
                  <span class="inline-price-warning__text">
                    {{ upcomingPriceChangeWarningText }}
                  </span>
                </div>
              </template>
            </template>
            <template v-else>
              <v-select
                attach
                hide-details
                outlined
                dense
                disabled
                label="Special Selection(s)"
                :background-color="backgroundColor"
              />
            </template>
          </td>

          <td>
            <calendar-field
              label="Start Date *"
              :date="special.startDate"
              :disabled="
                !(special.selectedProduct || special.selectedFamilyGroup)
              "
              :rules="[VALID_START_DATE(special.startDate, special.endDate)]"
              :error="!special.startDate || isDuplicate"
              :min="minAllowedStartDate"
              @dateChanged="onFieldChange('startDate', $event)"
            />
          </td>

          <td>
            <calendar-field
              label="End Date *"
              :date="special.endDate"
              :disabled="!special.startDate"
              :error="!special.endDate || isDuplicate"
              :rules="[VALID_END_DATE(special.startDate, special.endDate)]"
              :min="minAllowedEndDate"
              @dateChanged="onFieldChange('endDate', $event)"
            />
          </td>

          <td>
            <v-select
              attach
              outlined
              required
              dense
              label="Select Type *"
              :background-color="backgroundColor"
              :error="!special.targetType || isDuplicate"
              :disabled="!special.endDate"
              :items="tenantTargetTypes"
              :rules="[(v) => !!v || 'Target Type required']"
              v-model="special.targetType"
              @change="
                (onFieldChange('targetType', $event), resetTargetTypeForm())
              "
            />
          </td>

          <td class="target-item-column">
            <template v-if="hasSelectedTargetType">
              <template v-if="special.targetType === TARGET_TYPE.BANNER_GROUP">
                <v-autocomplete
                  :background-color="backgroundColor"
                  chips
                  clearable
                  deletable-chips
                  dense
                  :disabled="!special.targetType"
                  :error="!hasBannerGroupsSelected || isDuplicate"
                  :items="targetBannerGroups"
                  item-text="displayName"
                  item-value="id"
                  label="Select Banner Group(s)"
                  multiple
                  outlined
                  :rules="[
                    (v) => (v && !!v.length) || 'Banner Group(s) required'
                  ]"
                  small-chips
                  v-model="special.targetBannerGroupIds"
                  @change="onFieldChange('targetBannerGroupIds', $event)"
                >
                  <template v-slot:selection="{ item, index }">
                    <template v-if="special.targetBannerGroupIds.length === 1">
                      <v-chip small>{{ item.displayName }}</v-chip>
                    </template>
                    <template
                      v-if="
                        special.targetBannerGroupIds.length >= 2 && index === 1
                      "
                    >
                      <v-chip small
                        >{{ special.targetBannerGroupIds.length }} BGs
                        selected</v-chip
                      >
                    </template>
                  </template>
                </v-autocomplete>
              </template>
              <template
                v-if="special.targetType === TARGET_TYPE.MULTI_STORE_OPERATOR"
              >
                <v-autocomplete
                  chips
                  clearable
                  deletable-chips
                  small-chips
                  multiple
                  outlined
                  dense
                  label="Select Multi-Store Operator(s)"
                  item-text="displayName"
                  item-value="id"
                  :background-color="backgroundColor"
                  :error="!hasMultiStoreOperatorsSelected || isDuplicate"
                  :disabled="!special.targetType"
                  :items="targetMultiStoreOperators"
                  :rules="[
                    (v) =>
                      (v && !!v.length) || 'Multi-Store Operator(s) required'
                  ]"
                  v-model="special.targetMultiStoreOperatorIds"
                  @change="onFieldChange('targetMultiStoreOperatorIds', $event)"
                >
                  <template v-slot:selection="{ item, index }">
                    <template
                      v-if="special.targetMultiStoreOperatorIds.length === 1"
                    >
                      <v-chip small>{{ item.displayName }}</v-chip>
                    </template>
                    <template
                      v-if="
                        special.targetMultiStoreOperatorIds.length >= 2 &&
                        index === 1
                      "
                    >
                      <v-chip small
                        >{{ special.targetMultiStoreOperatorIds.length }} MSOs
                        selected</v-chip
                      >
                    </template>
                  </template>
                </v-autocomplete>
              </template>
              <template
                v-if="special.targetType === TARGET_TYPE.RETAILER_STORE"
              >
                <v-autocomplete
                  chips
                  clearable
                  deletable-chips
                  small-chips
                  multiple
                  outlined
                  dense
                  :label="`Select ${isILR ? 'Customer(s)' : 'Retailer(s)'}`"
                  item-text="displayName"
                  item-value="id"
                  :background-color="backgroundColor"
                  :error="!hasTargetStoresSelected || isDuplicate"
                  :disabled="!special.targetType"
                  :items="targetStores"
                  :rules="[
                    (v) =>
                      (v && !!v.length) ||
                      `${isILR ? 'Customer(s)' : 'Retailer(s)'} required`
                  ]"
                  v-model="special.targetStoreIds"
                  @change="handleRetailerChange"
                >
                  <template v-slot:selection="{ item, index }">
                    <template v-if="special.targetStoreIds.length === 1">
                      <v-chip small>{{ item.displayName }}</v-chip>
                    </template>
                    <template
                      v-if="special.targetStoreIds.length >= 2 && index === 1"
                    >
                      <v-chip small
                        >{{ special.targetStoreIds.length }}
                        {{ isILR ? 'Customers' : 'Retailers' }} selected</v-chip
                      >
                    </template>
                  </template>
                </v-autocomplete>
                <div class="retailer-action-button-wrap">
                  <retailer-list-action-button
                    class="mt-n1 mb-2"
                    :supplier-id="selectedSupplierId"
                    :selected-retailers="special.targetStoreIds"
                    mode="useOnly"
                    :saved-list-applied="special.savedListApplied"
                    @apply="applySavedRetailers"
                  />
                </div>
              </template>
            </template>
            <template v-else>
              <v-autocomplete
                :background-color="backgroundColor"
                hide-details
                label="Target Selection(s)"
                outlined
                dense
                disabled
              ></v-autocomplete>
            </template>
          </td>

          <td class="pricing-column">
            <v-btn
              class="pricing-button"
              :disabled="!canShowPricing"
              @click="onRowClick"
            >
              <template v-if="isExpand">
                <span>Hide</span>
                <v-icon small class="ml-1">mdi-arrow-up</v-icon>
              </template>
              <template v-else>
                <span>Show</span>
                <v-icon small class="ml-1">mdi-arrow-down</v-icon>
              </template>
            </v-btn>
          </td>

          <td class="validation-column">
            <div
              class="validation-background"
              :class="{ valid: isRowValid, invalid: !isRowValid }"
            >
              <v-icon v-if="isRowValid" color="green" class="pt-2"
                >mdi-check-circle-outline</v-icon
              >
              <v-icon v-else color="red" class="pt-2"
                >mdi-alert-circle-outline</v-icon
              >
            </div>
          </td>
        </tr>

        <template v-if="isExpandable && isExpand">
          <tr>
            <td colspan="9" class="px-2 py-1">
              <legacy-radio-group
                v-model="discountEntryMode"
                name="multi-special-discount-entry-mode"
                :options="discountEntryModeOptions"
                :default-col-props="{ cols: '12', sm: '3', class: 'py-0' }"
                class="mt-3 mb-2"
              />
              <update-price-to-all-zones
                mode="special"
                :hide-unit-rrp="isServiceProductSelected || isUSCountry"
                :is-service-product="isServiceProductSelected"
                v-model:discount-entry-mode="discountEntryMode"
                @update-carton="
                  (val) => {
                    special.updateCartonPrice = val
                    special.regionalPriceMap.forEach((zone) => {
                      zone.amountOff = val
                      setDiscountPercentageFromAmountOff(
                        zone,
                        zone.cartonPrice,
                        val
                      )

                      const refArray = $refs[`regionZoneRef${zone.code}`]
                      const ref = Array.isArray(refArray)
                        ? refArray[0]
                        : refArray
                      if (ref && typeof ref.calcPricingForZone === 'function') {
                        ref.calcPricingForZone(zone)
                      }
                    })
                  }
                "
                @update-discount-percentage="
                  (val) => {
                    special.updateDiscountPercentage = val
                    special.regionalPriceMap.forEach((zone) => {
                      zone.discountPercentage = val
                      zone.amountOff = amountOffFromDiscountPercentage(
                        zone.cartonPrice,
                        val
                      )

                      const refArray = $refs[`regionZoneRef${zone.code}`]
                      const ref = Array.isArray(refArray)
                        ? refArray[0]
                        : refArray
                      if (ref && typeof ref.calcPricingForZone === 'function') {
                        ref.calcPricingForZone(zone)
                      }
                    })
                  }
                "
                @update-rrp="
                  (val) => {
                    special.updateUnitRrp = val
                    special.regionalPriceMap.forEach((zone) => {
                      zone.unitRrp = val

                      const refArray = $refs[`regionZoneRef${zone.code}`]
                      const ref = Array.isArray(refArray)
                        ? refArray[0]
                        : refArray
                      if (ref && typeof ref.calcPricingForZone === 'function') {
                        ref.calcPricingForZone(zone)
                      }
                    })
                  }
                "
              />
            </td>
          </tr>
          <tr>
            <td colspan="9" class="pricing-subheader-cell">
              <table
                class="multi-specials-data-table-headers"
                :class="{ 'service-mode': isServiceProductSelected }"
              >
                <thead>
                  <tr>
                    <th class="header-cell zone-column">Zone</th>
                    <th class="header-cell carton-price-column">
                      {{
                        isServiceProductSelected
                          ? 'PRICE (EX TAX)'
                          : 'CARTON PRICE (EX TAX)'
                      }}
                    </th>
                    <th class="header-cell amount-off-column">
                      {{
                        isServiceProductSelected
                          ? 'AMOUNT OFF (EX TAX)'
                          : 'AMOUNT OFF CARTON (EX TAX)'
                      }}
                    </th>
                    <th class="header-cell revised-unit-ex-tax-column">
                      REVISED UNIT<br />COST (EX TAX)
                    </th>
                    <th
                      v-if="!isUSCountry"
                      class="header-cell revised-unit-inc-tax-column"
                    >
                      REVISED UNIT<br />COST (INC TAX)
                    </th>
                    <th
                      v-if="!isServiceProductSelected && !isUSCountry"
                      class="header-cell original-rrp-column"
                    >
                      ORIGINAL UNIT RRP (INC TAX)
                    </th>
                    <th
                      v-if="!isServiceProductSelected && !isUSCountry"
                      class="header-cell special-unit-column"
                    >
                      SPECIAL UNIT RRP (INC TAX)
                    </th>
                    <th
                      v-if="!isServiceProductSelected && !isUSCountry"
                      class="header-cell gpp-column"
                    >
                      GP%
                    </th>
                  </tr>
                </thead>
              </table>
            </td>
          </tr>

          <tr>
            <td colspan="9" class="price-alerts">
              <v-alert
                v-if="hasNoPricedZones"
                border="left"
                dense
                outlined
                text
                type="error"
                elevation="1"
                class="mt-1 mb-3 i-alert"
              >
                <span>At least 1 zone with valid pricing is required</span>
              </v-alert>
              <v-alert
                v-else-if="hasIncompletePricing"
                border="left"
                dense
                outlined
                text
                type="error"
                elevation="1"
                class="mt-1 mb-3 i-alert"
              >
                <span>Zone(s) with incomplete pricing</span>
              </v-alert>
              <v-alert
                v-else-if="hasInvalidPricing"
                border="left"
                dense
                outlined
                text
                type="error"
                elevation="1"
                class="mt-1 mb-3 i-alert"
              >
                <span>Zone(s) with invalid pricing</span>
              </v-alert>
            </td>
          </tr>
        </template>
      </thead>

      <tbody>
        <tr v-if="isExpandable && isExpand">
          <td colspan="9" class="pt-0">
            <div class="multi-specials-pricing-div">
              <v-simple-table light dense>
                <template v-slot:default>
                  <tbody>
                    <tr
                      v-for="zone in special.regionalPriceMap"
                      :key="zone.code"
                    >
                      <special-region-zone
                        :zone="zone"
                        :is-duplicate="isDuplicate"
                        :error="!hasMoreThanOneZoneValue"
                        :is-service-product="isServiceProductSelected"
                        :selected-product="special.selectedProduct"
                        :discount-entry-mode="discountEntryMode"
                        :ref="`regionZoneRef${zone.code}`"
                      />
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </v-card>
</template>

<script>
import { defineComponent, nextTick } from 'vue'

import CalendarField from '@/components/CalendarField'
import { mapState, mapGetters } from 'vuex'
import {
  TARGET_TYPES,
  SPECIAL_TYPE,
  TARGET_TYPE
} from '@/modules/dataMaintenance/constants/Special.constants'
import {
  minAllowedPromotionEndDate,
  minAllowedPromotionStartDate
} from '@/modules/dataMaintenance/helper/PromotionPublishSchedule'
import SpecialRegionZone from '@/modules/dataMaintenance/components/SpecialRegionZone'
import SpecialsHelper from '@/modules/dataMaintenance/helper/SpecialsHelper'
import RetailerListActionButton from '@/components/RetailerListActionButton.vue'
import UpdatePriceToAllZones from '@/components/UpdatePriceToAllZones.vue'
import LegacyRadioGroup from '@/components/LegacyRadioGroup.vue'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'
import { buildVisibleProductOptions } from '@/modules/dataMaintenance/helper/ProductAutocompleteHelper'
import {
  buildUpcomingPriceChangeProductCodeSet,
  familyGroupHasUpcomingPriceChange,
  productHasUpcomingPriceChange,
  UPCOMING_PRICE_CHANGE_WARNING_TEXT
} from '@/modules/dataMaintenance/helper/PromotionProductAvailabilityHelper'
import {
  amountOffFromDiscountPercentage,
  DISCOUNT_ENTRY_MODE,
  setDiscountPercentageFromAmountOff
} from '@/modules/dataMaintenance/helper/DiscountEntryHelper'

const DATES_RULES = {
  VALID_START_DATE: (startDate, endDate) => {
    if (!startDate || !endDate) return true
    return endDate >= startDate || 'Start Date must be on or before End Date'
  },
  VALID_END_DATE: (startDate, endDate) => {
    if (!startDate && !endDate) return true
    if (!endDate) return 'End Date required'
    return endDate >= startDate || 'End Date must be on or after Start Date'
  }
}

export default defineComponent({
  emits: [
    'add',
    'remove',
    'duplicate',
    'change',
    'ensure-products',
    'search-products'
  ],

  components: {
    SpecialRegionZone,
    CalendarField,
    LegacyRadioGroup,
    RetailerListActionButton,
    UpdatePriceToAllZones
  },

  props: {
    familyGroups: Array,
    isDuplicate: Boolean,
    products: Array,
    productsLoading: {
      type: Boolean,
      default: false
    },
    special: { type: Object, required: true },
    specialPosition: { type: Number, required: true },
    specialsQty: { type: Number, required: true },
    isOnlyRow: Boolean,
    initialValue: Object,
    isRowValid: Boolean
  },

  data: () => ({
    backgroundColor: '#0000000f',
    specialTypes: [
      {
        title: 'By Family Group',
        key: SPECIAL_TYPE.BY_FAMILY_GROUP,
        text: 'By Family Group',
        value: SPECIAL_TYPE.BY_FAMILY_GROUP
      },
      {
        title: 'By Product',
        key: SPECIAL_TYPE.BY_PRODUCT,
        text: 'By Product',
        value: SPECIAL_TYPE.BY_PRODUCT
      }
    ],

    selectedFamilyGroup: null,
    selectedProduct: null,
    selectedSpecialType: null,
    startDate: null,
    endDate: null,
    targetType: null,
    targetBannerGroupIds: [],
    targetMultiStoreOperatorIds: [],
    targetStoreIds: [],
    savedListApplied: false,
    targetStores: [],
    targetBannerGroups: [],
    targetMultiStoreOperators: [],
    productSearchInput: '',
    discountEntryMode: DISCOUNT_ENTRY_MODE.AMOUNT,
    TARGET_TYPES,
    TARGET_TYPE,
    isExpand: false,
    ...DATES_RULES,
    ...SPECIAL_TYPE,
    upcomingPriceChangeWarningText: UPCOMING_PRICE_CHANGE_WARNING_TEXT
  }),

  computed: {
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('stores', ['retailerStores']),
    ...mapGetters('tenant', ['isILR', 'tenantCountryRegion']),
    ...mapState('preferences', ['selectedSupplierId']),

    upcomingPriceChangeProductCodes() {
      return buildUpcomingPriceChangeProductCodeSet(
        this.products,
        this.familyGroups
      )
    },

    tenantTargetTypes() {
      return TARGET_TYPES.map((type) => {
        if (type.value === 'RETAILER_STORE') {
          return {
            ...type,
            text: this.isILR ? 'Customer(s)' : 'Retailer(s)'
          }
        }
        return type
      })
    },

    discountEntryModeOptions() {
      return [
        { label: 'Amount', value: DISCOUNT_ENTRY_MODE.AMOUNT },
        { label: '% Discount', value: DISCOUNT_ENTRY_MODE.PERCENTAGE }
      ]
    },

    // ***
    // Special related validators
    // ***

    canShowPricing() {
      return (
        (this.special?.selectedFamilyGroup || this.special?.selectedProduct) &&
        (this.hasBannerGroupsSelected ||
          this.hasMultiStoreOperatorsSelected ||
          this.hasTargetStoresSelected ||
          this.TARGET_TYPE.NATIONAL)
      )
    },

    hasBannerGroupsSelected() {
      return this.special?.targetBannerGroupIds?.length > 0
    },

    hasMultiStoreOperatorsSelected() {
      return this.special?.targetMultiStoreOperatorIds?.length > 0
    },

    hasTargetStoresSelected() {
      return this.special?.targetStoreIds?.length > 0
    },

    hasSelectedSpecialType() {
      return [this.BY_FAMILY_GROUP, this.BY_PRODUCT]?.includes(
        this.special.selectedSpecialType
      )
    },

    hasSelectedTargetType() {
      return Object.values(this.TARGET_TYPE)?.includes(this.special.targetType)
    },

    isProductSelectionLoading() {
      return this.productsLoading && !this.products.length
    },

    isProductSelectionDisabled() {
      return !this.special.selectedSpecialType || this.isProductSelectionLoading
    },

    visibleProducts() {
      return buildVisibleProductOptions({
        options: this.products,
        searchTerm: this.productSearchInput,
        selectedValues: this.special?.selectedProduct
          ? [this.special.selectedProduct]
          : []
      })
    },

    showUnavailableProductAlert() {
      return (
        this.products.some((product) => product.disabled) ||
        this.familyGroups.some((familyGroup) => familyGroup.disabled)
      )
    },

    isServiceProductSelected() {
      return this.special?.selectedProduct?.productType === 'SERVICE'
    },

    hasNoPricedZones() {
      if (this.isServiceProductSelected) {
        return !this.special?.regionalPriceMap?.find(
          (zone) => zone.amountOff > 0 && zone.amountOff < zone.cartonPrice
        )
      }
      return SpecialsHelper.hasNoPricedZones(this.special?.regionalPriceMap)
    },

    hasIncompletePricing() {
      if (this.isServiceProductSelected) return false
      return SpecialsHelper.hasIncompletePricing(this.special?.regionalPriceMap)
    },

    hasInvalidPricing() {
      if (this.isServiceProductSelected) {
        return !!this.special?.regionalPriceMap?.find(
          (zone) => zone.amountOff < 0 || zone.amountOff >= zone.cartonPrice
        )
      }
      return SpecialsHelper.hasInvalidPricing(this.special?.regionalPriceMap)
    },

    hasMoreThanOneZoneValue() {
      if (this.isServiceProductSelected) {
        return this.special?.regionalPriceMap?.find((p) => p.amountOff)
      }
      return this.special?.regionalPriceMap?.find(
        (p) => p.amountOff && p.unitRrp
      )
    },

    isExpandable() {
      return this.special.regionalPriceMap.length > 0
    },

    isInitialValue() {
      return JSON.stringify(this.initialValue) === JSON.stringify(this.special)
    },

    minAllowedEndDate() {
      return minAllowedPromotionEndDate(
        this.special?.startDate,
        this.tenantCountryRegion
      )
    },

    minAllowedStartDate() {
      return minAllowedPromotionStartDate(this.tenantCountryRegion)
    },

    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },

    isUSCountry() {
      return this.displayRules.isUSCountry
    }
  },

  watch: {
    discountEntryMode(newMode, oldMode) {
      if (oldMode && newMode !== oldMode) {
        this.clearDiscountPricing()
      }
    },

    productSearchInput(value) {
      if (this.special.selectedSpecialType !== this.BY_PRODUCT) return
      this.$emit('search-products', value || '')
    }
  },

  methods: {
    amountOffFromDiscountPercentage,
    setDiscountPercentageFromAmountOff,

    emitEnsureProducts() {
      this.$emit('ensure-products')
    },

    clearSelectedProduct() {
      this.productSearchInput = ''
      this.$emit('change', {
        selectedProduct: null,
        regionalPriceMap: []
      })
      this.$emit('search-products', '')
    },

    handleProductChange(value) {
      if (
        productHasUpcomingPriceChange(
          value,
          this.familyGroups,
          this.upcomingPriceChangeProductCodes
        ) ||
        value?.disabled === true
      ) {
        this.clearSelectedProduct()
        return
      }

      this.onFieldChange('selectedProduct', value)
      this.productSearchInput = ''
    },

    onRowClick() {
      this.isExpand = !this.isExpand
    },

    onDuplicateClick() {
      if (this.isDuplicate) {
        return
      }
      this.$emit('duplicate')
    },

    resetTargetTypeForm() {
      this.targetBannerGroupIds = []
      this.targetMultiStoreOperatorIds = []
      this.targetStoreIds = []
    },

    onFieldChange(fieldName, value) {
      if (fieldName === 'specialTypes') {
        this.$emit('change', {
          [fieldName]: value,
          selectedProduct: null,
          selectedFamilyGroup: null,
          regionalPriceMap: []
        })
      } else if (fieldName === 'targetType') {
        this.$emit('change', {
          [fieldName]: value,
          targetBannerGroupIds: [],
          targetMultiStoreOperatorIds: [],
          targetStoreIds: []
        })
      } else {
        if (
          fieldName === 'selectedFamilyGroup' &&
          familyGroupHasUpcomingPriceChange(
            value,
            this.upcomingPriceChangeProductCodes
          )
        ) {
          this.$emit('change', {
            selectedFamilyGroup: null,
            regionalPriceMap: []
          })
          return
        }

        this.$emit('change', {
          [fieldName]: value
        })
      }
    },

    clearDiscountPricing() {
      const regionalPriceMap = (this.special.regionalPriceMap || []).map(
        (zone) => {
          const nextZone = { ...zone }
          nextZone.amountOff = null
          delete nextZone.discountPercentage
          nextZone.grossProfitPercentage = null
          nextZone.revisedUnitPriceExTax = null
          nextZone.revisedUnitPriceIncTax = null
          return nextZone
        }
      )
      this.$emit('change', { regionalPriceMap })
    },

    mapStoresList() {
      if (this.retailerStores?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetStores = this.retailerStores.sort((a, b) =>
          a.displayName > b.displayName ? 1 : -1
        )

        if (this.special.regionalPriceMap?.length) {
          // Filter out stores not located in regions where the product is available
          this.targetStores = this.targetStores.filter((store) =>
            this.special.regionalPriceMap
              .map((p) => p.code)
              .includes(store.pricingRegionCode)
          )
        }
      } else {
        this.addNotification({
          color: 'secondary',
          text: `Available ${
            this.isILR ? 'Customers' : 'Retailers'
          } loading, please wait...`
        })
      }
    },

    mapBannerGroupsList() {
      if (this.bannerGroups?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetBannerGroups = this.bannerGroups.sort((a, b) =>
          a.displayName > b.displayName ? 1 : -1
        )
      } else {
        this.addNotification({
          color: 'secondary',
          text: 'Available Banner Groups loading, please wait...'
        })
      }
    },

    mapMultiStoreOperatorsList() {
      if (this.multiStoreOperators?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetMultiStoreOperators = this.multiStoreOperators.sort(
          (a, b) => (a.displayName > b.displayName ? 1 : -1)
        )
      } else {
        this.addNotification({
          color: 'secondary',
          text: 'Available MSOs loading, please wait...'
        })
      }
    },

    applySavedRetailers(ids) {
      this.special.targetStoreIds = [...ids]
      this.special.savedListApplied = true
    },

    handleRetailerChange(value) {
      this.special.savedListApplied = false
      this.onFieldChange('targetStoreIds', value)
    },

    resetSpecial() {
      if (!this.initialValue) return

      const reset = JSON.parse(JSON.stringify(this.initialValue))

      this.special.selectedSpecialType = reset.selectedSpecialType
      this.special.selectedProduct = reset.selectedProduct
      this.special.selectedFamilyGroup = reset.selectedFamilyGroup
      this.special.startDate = reset.startDate
      this.special.endDate = reset.endDate
      this.special.targetType = reset.targetType
      this.special.targetBannerGroupIds = [
        ...(reset.targetBannerGroupIds || [])
      ]

      this.special.targetMultiStoreOperatorIds = [
        ...(reset.targetMultiStoreOperatorIds || [])
      ]

      this.special.targetStoreIds = [...(reset.targetStoreIds || [])]
      this.special.savedListApplied = reset.savedListApplied || false
      this.special.regionalPriceMap = JSON.parse(
        JSON.stringify(reset.regionalPriceMap || [])
      )

      this.isExpand = false

      // Recalculate pricing per zone
      nextTick(() => {
        ;(this.special.regionalPriceMap || []).forEach((zone) => {
          const refArray = this.$refs[`regionZoneRef${zone.code}`]
          const ref = Array.isArray(refArray) ? refArray[0] : refArray
          if (ref && typeof ref.calcPricingForZone === 'function') {
            ref.calcPricingForZone(zone)
          }
        })
      })
    }
  },

  mounted() {
    this.mapStoresList()
    this.mapBannerGroupsList()
    this.mapMultiStoreOperatorsList()
  }
})
</script>

<style lang="scss">
#multi-specials-table {
  .v-select.v-select--chips:not(
      .v-text-field--single-line
    ).v-text-field--enclosed.v-select--chips--small.v-input--dense
    .v-select__selections {
    min-height: 30px !important;
    max-height: 30px !important;
  }

  .v-select.v-select--chips.v-input--dense .v-select__selections {
    max-height: 30px !important;
  }

  .v-text-field__details {
    padding: 0 !important;
    div.v-messages__message {
      font-size: 0.9rem !important;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#multi-specials-table {
  border-collapse: collapse;
  background-color: $bg-secondary-color;
  width: 100%;
  min-width: 1200px;
  table-layout: fixed;
  td:not(.price-alerts) {
    vertical-align: top;
    text-align: center;
    padding: 4px;
  }
  th {
    padding: 8px 8px 0 8px;
    white-space: nowrap;
  }
  .multi-specials-pricing-div {
    max-height: 530px;
    overflow-y: scroll;
    overflow-x: hidden;
    :deep(.v-data-table__wrapper > table) {
      width: calc(100% + 14px) !important;
      table-layout: fixed;
    }
  }
  .multi-specials-table-header {
    background-color: $bg-secondary-color;
    .actions-column {
      width: 8%;
      min-width: 150px;
      text-align: center !important;
      &:before {
        width: 8%;
        min-width: 150px;
      }
    }
    .action-icon {
      color: $primary-color;
    }
    .copy-icon {
      color: $primary-color;
      &.duplicate {
        color: $red-color !important;
      }
    }
    .expand-icon,
    .collapse-icon {
      &.expandable {
        color: $primary-color;
      }
    }
    .header-cell:before {
      content: ' ';
      display: block;
    }
    .multi-specials-data-row {
      &.body-expandable:hover {
        cursor: pointer;
      }
    }
    .multi-specials-title-row {
      th {
        text-align: left;
        text-transform: uppercase;
        letter-spacing: 0.25px;
        padding-bottom: 4px;
      }
    }
    .multi-specials-data-table-headers {
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      th {
        border-top: 2px solid $primary-color;
        padding: 16px 8px 4px 8px;
      }
      .zone-column,
      .carton-price-column,
      .revised-unit-ex-tax-column,
      .revised-unit-inc-tax-column,
      .original-rrp-column,
      .gpp-column {
        width: 12.5% !important;
        text-transform: uppercase;
        white-space: unset;
      }
      .amount-off-column,
      .special-unit-column {
        width: 12.5% !important;
        text-transform: uppercase;
        white-space: unset;
      }
      &.service-mode {
        .zone-column,
        .carton-price-column,
        .amount-off-column,
        .revised-unit-ex-tax-column,
        .revised-unit-inc-tax-column {
          width: 20% !important;
        }
      }
    }
    .pricing-subheader-cell {
      padding: 0 !important;
    }
    .pricing-column {
      width: 85px !important;
      text-align: center !important;
      &:before {
        width: 85px;
      }
      .pricing-button {
        background-color: $primary-color;
        color: white;
        text-transform: none;
        font-size: 14px;
        font-weight: bold;
        width: 75px;
        height: 39px;
      }
    }
    .start-date-column,
    .end-date-column {
      width: 10%;
      min-width: 130px;
      &:before {
        width: 10%;
        min-width: 130px;
      }
    }
    .special-type-column {
      width: 11%;
      min-width: 175px;
      &:before {
        width: 11%;
        min-width: 175px;
      }
    }
    .special-item-column {
      overflow: visible;
      position: relative;
      width: 16%;
      min-width: 200px;
      &:before {
        width: 16%;
        min-width: 200px;
      }

      :deep(.v-select__selection) {
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      :deep(.v-input__slot) {
        min-height: 40px !important;
      }

      .inline-price-warning {
        align-items: center;
        color: $warning-color;
        display: flex;
        font-size: 12px;
        line-height: 16px;
        margin-top: -14px;
        max-width: none;
        min-height: 16px;
        overflow: visible;
        padding: 0 4px;
        position: absolute;
        text-align: left;
        width: 760px;
        z-index: 1;

        &__icon {
          color: $warning-color;
          flex: 0 0 auto;
          margin-right: 4px;
        }

        &__text {
          display: block;
          min-width: 0;
          overflow: visible;
          text-overflow: clip;
          white-space: nowrap;
        }
      }
    }
    .target-type-column {
      width: 16%;
      min-width: 200px;
      &:before {
        width: 16%;
        min-width: 200px;
      }
    }
    .target-item-column {
      width: 20%;
      min-width: 200px;
      &:before {
        width: 20%;
        min-width: 200px;
      }
    }
    .validation-column {
      width: 70px !important;
      text-align: center !important;
      &:before {
        width: 70px;
      }
      .validation-background {
        border-radius: 3px;
        margin: auto;
        height: 39px;
        width: 60px;
      }
      .valid {
        background-color: $green-color-bg;
      }
      .invalid {
        background-color: $red-color-bg;
      }
    }
  }
  .retailer-action-button-wrap {
    display: flex;
    justify-content: flex-start;
    margin-left: 4px;
    margin-top: -16px;
  }
}
</style>
